import { useEffect, useState } from "react";
import {
  Phone, PhoneOff, MessageSquare, Voicemail, Mail, FileText, ArrowRight,
  Calendar as CalIcon, Plus, Sparkles, Clock, CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * LeadActivityLog — append-only timeline for a single lead.
 *
 * Renders auto-events (created, status_changed, followup_set) plus manual touches
 * (called/talked, called/VM, called/no-answer, texted, emailed, appointment-held, note).
 *
 * Logging a touch can optionally reschedule the lead's next_follow_up in the same call.
 */

const TOUCH_OPTIONS = [
  { type: "called_talked",    label: "Talked",     icon: Phone,         color: "#22c55e", needsText: false },
  { type: "called_vm",        label: "VM",         icon: Voicemail,     color: "#a78bfa", needsText: false },
  { type: "called_no_answer", label: "Missed",     icon: PhoneOff,      color: "#94a3b8", needsText: false },
  { type: "texted",           label: "Texted",     icon: MessageSquare, color: "#60a5fa", needsText: false },
  { type: "emailed",          label: "Emailed",    icon: Mail,          color: "#fbbf24", needsText: false },
  { type: "appointment_held", label: "Appt",       icon: CalIcon,       color: "#f97316", needsText: false },
  { type: "note",             label: "Note",       icon: FileText,      color: "#cbd5e1", needsText: true  },
];

const RESCHEDULE_CHIPS = [
  { label: "+1d", days: 1 },
  { label: "+3d", days: 3 },
  { label: "+1wk", days: 7 },
  { label: "+2wk", days: 14 },
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (isToday) return `Today ${time}`;
  if (isYesterday) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${time}`;
}

function addDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function LeadActivityLog({ leadId, onLeadUpdated }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null);   // touch option selected → opens detail form
  const [text, setText] = useState("");
  const [reschedule, setReschedule] = useState(null); // chip days or null
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}/events`);
      setEvents(data);
    } catch (e) {
      // Ignore — empty timeline is fine.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [leadId]);

  const submit = async () => {
    if (!picker) return;
    if (picker.needsText && !text.trim()) {
      toast.error("Add a quick description");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        type: picker.type,
        text: text.trim() || null,
        next_follow_up: reschedule ? addDays(reschedule) : null,
      };
      await api.post(`/leads/${leadId}/events`, body);
      setPicker(null);
      setText("");
      setReschedule(null);
      await load();
      if (onLeadUpdated) onLeadUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't log");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="lead-activity-log">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: "var(--lime)" }} />
          <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text)" }}>
            Activity log
          </span>
        </div>
        {!picker && (
          <button
            onClick={() => setPicker(TOUCH_OPTIONS[0])}
            className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition"
            style={{ background: "var(--lime)", color: "#0c0c0c" }}
            data-testid="activity-log-add"
          >
            <Plus size={12} /> Log Contact
          </button>
        )}
      </div>

      {picker && (
        <div
          className="rounded-2xl p-4 mb-4"
          style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
          data-testid="activity-log-form"
        >
          <div className="grid grid-cols-7 gap-1.5 mb-3">
            {TOUCH_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isActive = picker.type === opt.type;
              return (
                <button
                  key={opt.type}
                  onClick={() => setPicker(opt)}
                  className="flex flex-col items-center gap-1 px-1 py-2 rounded-xl transition"
                  style={{
                    background: isActive ? "var(--lime-glow)" : "transparent",
                    border: isActive ? "1px solid var(--lime)" : "1px solid var(--border)",
                  }}
                  data-testid={`activity-touch-${opt.type}`}
                >
                  <Icon size={14} style={{ color: isActive ? "var(--lime)" : opt.color }} />
                  <span className="text-[10px] font-mono whitespace-nowrap" style={{ color: isActive ? "var(--lime)" : "var(--text-muted)" }}>
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder={picker.needsText ? "What's the note?" : "Anything to remember? (optional)"}
            className="input-base text-sm"
            data-testid="activity-log-text"
          />

          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Clock size={12} style={{ color: "var(--text-muted)" }} />
              <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                Follow up
              </span>
              {RESCHEDULE_CHIPS.map((chip) => {
                const isActive = reschedule === chip.days;
                return (
                  <button
                    key={chip.days}
                    onClick={() => setReschedule(isActive ? null : chip.days)}
                    className="px-2.5 py-1 rounded-full text-[11px] font-mono transition"
                    style={{
                      background: isActive ? "var(--lime)" : "transparent",
                      border: isActive ? "1px solid var(--lime)" : "1px solid var(--border)",
                      color: isActive ? "#0c0c0c" : "var(--text-muted)",
                    }}
                    data-testid={`activity-reschedule-${chip.days}`}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setPicker(null); setText(""); setReschedule(null); }}
                className="text-xs px-3 py-1.5 rounded-xl"
                style={{ color: "var(--text-muted)" }}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="text-xs font-semibold px-4 py-1.5 rounded-xl flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: "var(--lime)", color: "#0c0c0c" }}
                data-testid="activity-log-submit"
              >
                {submitting ? "Logging..." : "Log it"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6"><span className="spinner" /></div>
      ) : events.length === 0 ? (
        <div className="text-center py-6 text-xs" style={{ color: "var(--text-dim)" }}>
          No activity yet — log your first touch above.
        </div>
      ) : (
        <ul className="space-y-3">
          {events.map((ev) => <EventRow key={ev.id} ev={ev} />)}
        </ul>
      )}
    </div>
  );
}

function EventRow({ ev }) {
  const meta = ev.meta || {};
  let Icon = FileText;
  let color = "var(--text-muted)";
  let title = "Activity";
  let body = ev.text;

  if (ev.type === "created") {
    Icon = Plus;
    color = "var(--lime)";
    title = "Lead created";
    body = meta.source ? `Source: ${meta.source.replace(/_/g, " ")}` : null;
  } else if (ev.type === "status_changed") {
    Icon = ArrowRight;
    color = "#60a5fa";
    title = "Status changed";
    body = `${(meta.from || "—").replace(/_/g, " ")} → ${(meta.to || "—").replace(/_/g, " ")}`;
  } else if (ev.type === "followup_set") {
    Icon = CalIcon;
    color = "#a78bfa";
    title = "Follow-up scheduled";
    body = meta.to ? `Next: ${meta.to}` : "Cleared";
  } else if (ev.type === "appointment_scheduled") {
    Icon = CalIcon;
    color = "#f97316";
    title = "Appointment scheduled";
    body = meta.when ? `${ev.text || "Meeting"} · ${meta.when}` : ev.text;
  } else if (ev.type === "task_added") {
    Icon = Plus;
    color = "#a78bfa";
    title = "Task added";
    body = meta.due_date ? `${ev.text} · due ${meta.due_date}` : ev.text;
  } else if (ev.type === "task_completed") {
    Icon = CheckCircle2;
    color = "#22c55e";
    title = "Task completed";
    body = ev.text;
  } else {
    const opt = TOUCH_OPTIONS.find((o) => o.type === ev.type);
    if (opt) { Icon = opt.icon; color = opt.color; title = opt.label; }
  }

  return (
    <li className="flex items-start gap-3" data-testid={`activity-event-${ev.id}`}>
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: "var(--surface2)", color, border: `1px solid ${color}33` }}
      >
        <Icon size={12} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold">{title}</span>
          <span className="font-mono text-[10px]" style={{ color: "var(--text-dim)" }}>{fmtDate(ev.created_at)}</span>
        </div>
        {body && (
          <p className="text-xs mt-0.5 whitespace-pre-wrap" style={{ color: "var(--text-muted)" }}>{body}</p>
        )}
      </div>
    </li>
  );
}
